<script setup lang="ts">
import { PhDatabase, PhTerminal, PhTrash, PhWarningDiamond } from "@phosphor-icons/vue"
import { computed, ref } from "vue"
import { useDashboardApi } from "../composables/useDashboardApi"
import type { DashboardMaintenanceAction } from "../composables/useDashboardApi"
import DashboardCard from "../components/DashboardCard.vue"
import DataState from "../components/DataState.vue"
import MetricTile from "../components/MetricTile.vue"
import PageHeader from "../components/PageHeader.vue"
import StatusBadge from "../components/StatusBadge.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useAuthStore } from "../stores/auth"
import { useDashboardStore } from "../stores/dashboard"
import { dashboardEnvironment } from "../utils/env"
import { formatRelative } from "../utils/format"
import { dashboardRuntimeVersion } from "../utils/version"

type MaintenanceTone = "success" | "warning" | "error" | "neutral"

const dashboard = useDashboardStore()
const auth = useAuthStore()
const confirmOpen = ref(false)
const pendingAction = ref<DashboardMaintenanceAction | null>(null)
const maintenanceRunning = ref(false)
const maintenanceMessage = ref("")
const maintenanceTone = ref<MaintenanceTone>("neutral")
const readyEndpoints = computed(
  () => dashboard.diagnosticsEndpoints.filter((endpoint) => endpoint.status === "ready").length
)
const maintenanceDisabled = computed(
  () => dashboardEnvironment.dataMode === "fixtures" || maintenanceRunning.value || dashboard.refreshInFlight
)
const workerVersionLabel = computed(() => {
  const version = dashboard.workerVersion
  return version ? `${version.reportsAs} ${version.version}` : "unavailable"
})
const workerCompatibility = computed(() => {
  const version = dashboard.workerVersion
  return version ? formatCompatibility(version.compatibility) : "unavailable"
})
const pendingActionCopy = computed(() => {
  if (pendingAction.value === "clear-session-credentials") {
    return {
      confirmLabel: "Clear tables",
      description:
        "This clears retained session and credential mirror rows from D1. KV source records are not deleted by this action.",
      title: "Clear session and credential tables"
    }
  }

  return {
    confirmLabel: "Clear cache",
    description: "This clears the dashboard snapshot and projection cache keys from KV. Source runtime records remain.",
    title: "Clear dashboard KV cache"
  }
})

function openMaintenance(action: DashboardMaintenanceAction) {
  pendingAction.value = action
  confirmOpen.value = true
}

async function confirmMaintenance(close: () => void) {
  if (!pendingAction.value || maintenanceRunning.value) {
    return
  }

  maintenanceRunning.value = true
  maintenanceMessage.value = ""

  try {
    const result = await useDashboardApi().runMaintenanceAction(pendingAction.value, auth.dashboardToken)
    if (pendingAction.value === "clear-session-credentials") {
      const tableCount = result.tables?.length ?? 0
      maintenanceMessage.value = result.d1Available
        ? `Cleared ${tableCount} D1 table${tableCount === 1 ? "" : "s"}.`
        : "No D1 binding was available; dashboard caches were cleared."
    } else {
      maintenanceMessage.value = `Cleared ${result.deletedKeys?.length ?? 0} KV cache keys.`
    }
    maintenanceTone.value = "success"
    close()
    await dashboard.refreshFromApi(auth.dashboardToken, { force: true, silent: true })
  } catch (error) {
    maintenanceMessage.value = error instanceof Error ? error.message : "Dashboard maintenance action failed."
    maintenanceTone.value = "error"
  } finally {
    maintenanceRunning.value = false
  }
}

function formatCompatibility(value: Record<string, string>): string {
  const entries = Object.entries(value)
  if (entries.length === 0) {
    return "none"
  }
  return entries.map(([component, range]) => `${component} ${range}`).join(", ")
}
</script>

<template>
  <DashboardLayout>
    <div class="grid gap-5">
      <PageHeader
        title="Developer diagnostics"
        description="Runtime mode, endpoints, and maintenance tools."
        details="Shows dashboard runtime mode, API target, JSON endpoint readiness, fixture state, and guarded cache maintenance actions."
      >
        <template #actions>
          <StatusBadge :status="dashboard.sourceLabel" />
          <span class="dashboard-mono text-sm text-(--dash-muted)">{{ dashboardEnvironment.apiTarget }}</span>
        </template>
      </PageHeader>

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="false"
        empty-title="No diagnostics"
        empty-description="Diagnostics are always available in the dashboard app."
      >
        <section class="grid gap-3 md:grid-cols-3">
          <MetricTile
            label="Ready endpoints"
            :value="String(readyEndpoints)"
            tone="success"
            caption="Control-plane JSON endpoints modeled"
          />
          <MetricTile
            label="Data mode"
            :value="dashboard.sourceLabel"
            tone="neutral"
            caption="Live API or intentional fixture mode"
          />
          <MetricTile
            label="Last refresh"
            :value="formatRelative(dashboard.lastUpdatedAt)"
            tone="neutral"
            caption="Store snapshot timestamp"
          />
        </section>

        <section class="grid gap-4 xl:grid-cols-2">
          <DashboardCard title="Maintenance actions" eyebrow="Guarded operations" static>
            <div class="grid gap-4">
              <div v-if="maintenanceMessage" class="flex items-center gap-2">
                <StatusBadge :status="maintenanceTone" />
                <span class="text-sm text-(--dash-muted)">{{ maintenanceMessage }}</span>
              </div>
              <div class="grid gap-2 sm:grid-cols-2">
                <UButton
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  block
                  :disabled="maintenanceDisabled"
                  :icon="PhTrash"
                  class="dashboard-button vhs-glitch-trigger"
                  @click="openMaintenance('clear-cache')"
                >
                  <span class="vhs-glitch-text" data-vhs-text="CLEAR KV CACHE">Clear KV cache</span>
                </UButton>
                <UButton
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  block
                  :disabled="maintenanceDisabled"
                  :icon="PhDatabase"
                  class="dashboard-button dashboard-button--danger vhs-glitch-trigger"
                  @click="openMaintenance('clear-session-credentials')"
                >
                  <span class="vhs-glitch-text" data-vhs-text="CLEAR SESSION TABLES">Clear session tables</span>
                </UButton>
              </div>
            </div>
          </DashboardCard>

          <DashboardCard title="Runtime surface" eyebrow="Environment" static>
            <div class="grid gap-3 text-sm">
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">Route base</span>
                <span class="dashboard-mono">{{ dashboardEnvironment.routeBase }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">API target</span>
                <span class="dashboard-mono">{{ dashboardEnvironment.apiTarget }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">API base</span>
                <span class="dashboard-mono truncate">{{ dashboardEnvironment.apiBaseUrl || "same-origin" }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">Fixture mode</span>
                <StatusBadge :status="dashboardEnvironment.fixtureFallback ? 'stable' : 'blocked'" />
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">Dashboard version</span>
                <span class="dashboard-mono"
                  >{{ dashboardRuntimeVersion.reportsAs }} {{ dashboardRuntimeVersion.version }}</span
                >
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">Worker version</span>
                <span class="dashboard-mono">{{ workerVersionLabel }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">Worker compatibility</span>
                <span class="dashboard-mono truncate">{{ workerCompatibility }}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-(--dash-muted)">Fallback reason</span>
                <span class="dashboard-mono truncate">{{ dashboard.apiErrorMessage || "none" }}</span>
              </div>
            </div>
          </DashboardCard>
        </section>

        <DashboardCard
          title="JSON endpoint readiness"
          eyebrow="Dashboard contracts"
          static
          class="dashboard-endpoint-card"
        >
          <div class="dashboard-scroll-area dashboard-endpoint-list">
            <div class="min-w-[680px]">
              <div
                class="dashboard-label grid grid-cols-[170px_minmax(0,1fr)_120px] gap-3 border-b border-(--dash-line) pb-3"
              >
                <span>Surface</span>
                <span>Path</span>
                <span>Status</span>
              </div>
              <div
                v-for="endpoint in dashboard.diagnosticsEndpoints"
                :key="endpoint.id"
                class="dashboard-row grid grid-cols-[170px_minmax(0,1fr)_120px] items-center gap-3 py-3 text-sm"
              >
                <span class="text-(--dash-ink)">{{ endpoint.label }}</span>
                <span class="dashboard-mono truncate text-(--dash-muted)">{{ endpoint.path }}</span>
                <StatusBadge :status="endpoint.status" />
              </div>
            </div>
          </div>
        </DashboardCard>
      </DataState>

      <UModal
        v-model:open="confirmOpen"
        :close="!maintenanceRunning"
        :dismissible="!maintenanceRunning"
        :title="pendingActionCopy.title"
        :description="pendingActionCopy.description"
        :ui="{
          overlay: 'dashboard-maintenance-modal-overlay',
          content: 'dashboard-maintenance-modal-content',
          header: 'dashboard-maintenance-modal-header',
          title: 'dashboard-maintenance-modal-title',
          description: 'dashboard-maintenance-modal-description',
          body: 'dashboard-maintenance-modal-body',
          footer: 'dashboard-maintenance-modal-footer'
        }"
      >
        <template #body>
          <div class="dashboard-maintenance-terminal">
            <div class="flex items-start gap-3">
              <PhWarningDiamond :size="20" class="mt-0.5 shrink-0 text-(--dash-danger)" />
              <div class="grid gap-2">
                <p>
                  confirm destructive maintenance command
                  <span class="dashboard-terminal-cursor" aria-hidden="true" />
                </p>
                <p>
                  target:
                  <span class="dashboard-maintenance-terminal-strong">{{ dashboardEnvironment.apiTarget }}</span>
                </p>
              </div>
            </div>
          </div>
        </template>
        <template #footer="{ close }">
          <div class="flex w-full justify-end gap-2">
            <UButton
              color="neutral"
              variant="ghost"
              size="sm"
              :disabled="maintenanceRunning"
              class="dashboard-button dashboard-maintenance-cancel-button vhs-glitch-trigger"
              @click="close"
            >
              <span class="vhs-glitch-text" data-vhs-text="CANCEL">Cancel</span>
            </UButton>
            <UButton
              color="neutral"
              variant="ghost"
              size="sm"
              :loading="maintenanceRunning"
              class="dashboard-button dashboard-maintenance-clear-button vhs-glitch-trigger"
              @click="confirmMaintenance(close)"
            >
              <span class="dashboard-maintenance-clear-label">
                <PhTerminal :size="15" />
                <span class="vhs-glitch-text" :data-vhs-text="pendingActionCopy.confirmLabel.toUpperCase()">
                  {{ pendingActionCopy.confirmLabel }}
                </span>
                <span class="dashboard-terminal-cursor" aria-hidden="true" />
              </span>
            </UButton>
          </div>
        </template>
      </UModal>
    </div>
  </DashboardLayout>
</template>
